<script setup>
import { ref, reactive, watch, computed, onMounted } from 'vue';
import { v4 as uuidv4 } from 'uuid';
import SlideoutWithSlots from '../elements/slideoutWithSlots.vue';
import axios from 'axios';
const emit = defineEmits(['update:prizes', 'save']);

const props = defineProps({
    modelValue: {
        type: Array,
        default: () => []
    }
});

const PRO_MONTH_VALUE = 149;

const prizes = ref(props.modelValue.length > 0 ? props.modelValue : []);

const monthsRemaining = ref(0);

const usedMonths = computed(() => {
    return prizes.value.reduce((total, prize) => {
        return total + (prize.proMonths * prize.winners);
    }, 0);
});

const socialMediaProfiles = ref([
    { name: 'Instagram', icon: 'fab fa-instagram', included: false, url: '', placeholder: 'instagram.com/yourusername' },
    { name: 'Facebook', icon: 'fab fa-facebook', included: false, url: '', placeholder: 'facebook.com/yourusername' },
    { name: 'Twitter/X', icon: 'fab fa-twitter', included: false, url: '', placeholder: 'twitter.com/yourusername' },
    { name: 'LinkedIn', icon: 'fab fa-linkedin', included: false, url: '', placeholder: 'linkedin.com/in/yourusername' },
    { name: 'TikTok', icon: 'fab fa-tiktok', included: false, url: '', placeholder: 'tiktok.com/@yourusername' },
    { name: 'Snapchat', icon: 'fab fa-snapchat', included: false, url: '', placeholder: 'snapchat.com/add/yourusername' },
    { name: 'YouTube', icon: 'fab fa-youtube', included: false, url: '', placeholder: 'youtube.com/@yourchannel' },
    { name: 'Pinterest', icon: 'fab fa-pinterest', included: false, url: '', placeholder: 'pinterest.com/yourusername' },
    { name: 'Medium', icon: 'fab fa-medium', included: false, url: '', placeholder: 'medium.com/@yourusername' },
]);

const editingPrize = ref(null);
const editingPrizeOriginal = ref(null);
const customSocialMedia = ref('');
const showMediaPicker = ref(false);
const mediaBankEntryId = ref(null)

// Get tomorrow's date for min date constraint
const minEndDate = new Date(new Date().setDate(new Date().getDate() + 1)).toISOString().slice(0, 10);
const defaultEndDate = new Date(new Date().setDate(new Date().getDate() + 30)).toISOString().slice(0, 10);
const endDate = ref(defaultEndDate);

const calculateBenefitsValue = (prize) => {
    return prize.benefits.reduce((total, benefit) => {
        return total + (Number(benefit.value) || 0);
    }, 0);
};

const calculatePrizeValue = (prize) => {
    const proMonthsValue = prize.proMonths * PRO_MONTH_VALUE;
    const benefitsValue = calculateBenefitsValue(prize);
    return proMonthsValue + benefitsValue;
};

const totalCompetitionValue = computed(() => {
    return prizes.value.reduce((total, prize) => {
        return total + calculatePrizeValue(prize);
    }, 0);
});

const sortedPrizes = computed(() => {
    return [...prizes.value].sort((a, b) => {
        return calculatePrizeValue(b) - calculatePrizeValue(a);
    });
});

const addPrize = () => {
    if (prizes.value.length < 5) {
        const newPrize = {
            id: uuidv4(),
            proMonths: 0,
            winners: 1,
            benefits: []
        };
        prizes.value.push(newPrize);
        emitUpdate();
        editPrize(newPrize);
    }
};

const removePrize = (index) => {
    const prizeToRemove = sortedPrizes.value[index];
    const originalIndex = prizes.value.findIndex(p => p.id === prizeToRemove.id);
    prizes.value.splice(originalIndex, 1);
    emitUpdate();
};

const editPrize = (prize) => {
    editingPrizeOriginal.value = prize;
    editingPrize.value = JSON.parse(JSON.stringify(prize));
};

const closeEditor = () => {
    editingPrize.value = null;
    editingPrizeOriginal.value = null;
};

const saveEditedPrize = () => {
    if (!editingPrize.value || !editingPrizeOriginal.value) return;

    const index = prizes.value.findIndex(p => p.id === editingPrizeOriginal.value.id);
    if (index !== -1) {
        prizes.value.splice(index, 1, editingPrize.value);
        emitUpdate();
    }

    closeEditor();
};

const getMonthsRemaining = async () => {
    const response = await axios.get(route('business-partner.competitionMonthsRemaining'));

    monthsRemaining.value = response.data.months;
}

const addBenefit = (prize) => {
    if (prize.benefits.length < 5) {
        prize.benefits.push({
            id: uuidv4(),
            description: '',
            value: 0
        });
    }
};

const removeBenefit = (prize, benefitIndex) => {
    prize.benefits.splice(benefitIndex, 1);
};

const addCustomSocialMedia = () => {
    if (customSocialMedia.value.trim()) {
        socialMediaProfiles.value.push({
            name: customSocialMedia.value.trim(),
            icon: 'fa-light fa-globe',
            included: true,
            url: '',
            placeholder: 'example.com/profile'
        });
        customSocialMedia.value = '';
    }
};

const saveCompetition = async () => {
    if (! competitionValidates.value) {
        return;
    }

    const sortedPrizesData = [...prizes.value].sort((a, b) => {
        return calculatePrizeValue(b) - calculatePrizeValue(a);
    });

    const competition = {
        prizes: sortedPrizesData,
        endDate: endDate.value,
        mediaBankEntryId: mediaBankEntryId.value,
        socialMediaProfiles: socialMediaProfiles.value
            .filter(profile => profile.included)
            .map(profile => ({
                name: profile.name,
                icon: profile.icon,
                url: profile.url
            }))
    };

    try {
        const response = await axios.post(route('business-partner.storeCompetition'), competition);

        if (response.status === 200) {
            // Reset form after successful save
            prizes.value = [];

            endDate.value = defaultEndDate;

            // Reset social media profiles
            socialMediaProfiles.value.forEach(profile => {
                if (profile.name === 'Instagram' || profile.name === 'Facebook') {
                    profile.included = true;
                } else {
                    profile.included = false;
                }
                profile.url = '';
            });

            socialMediaProfiles.value = socialMediaProfiles.value.filter(profile =>
                ['Instagram', 'Facebook', 'Twitter/X', 'LinkedIn', 'TikTok', 'YouTube', 'Pinterest', 'Medium'].includes(profile.name)
            );

            emitUpdate();

            window.location.reload();

        }
    } catch (error) {
        console.error('Error saving competition:', error);
    }
};

const socialMediaProfilesValidate = computed(() => {
    let validates = true
    socialMediaProfiles.value.forEach(profile => {
        if (profile.included && !profile.url) {
            validates = false;
        }
    });

    return validates
});

const editingPrizeValidates = computed(() => {
    if (!editingPrize.value) return false;

    let validates = true;
    if (editingPrize.value.proMonths < 1) {
        validates = false;
    }

    editingPrize.value.benefits.forEach(benefit => {
        if (!benefit.description || isNaN(parseInt(benefit.value, 10))) {
            validates = false;
        }
    });

    return validates;
})

const competitionValidates = computed(() => {
    if (!endDate.value) {
        return false;
    }

    if (prizes.value.length < 1) {
        return false;
    }
    if (! socialMediaProfilesValidate.value) {
        return false;
    }

    return true;
})

const emitUpdate = () => {
    emit('update:prizes', prizes.value);
};

watch(prizes, () => {
    emitUpdate();
}, { deep: true });

onMounted(async () => {
    await getMonthsRemaining();
});
</script>

<template>
    <div class="competition-prizes">
        <div class="grid grid-cols-1 gap-8 md:grid-cols-2">
            <div>
                <h3>Poster image</h3>
                <div class="mb-4">
                    <div
                        v-if="showMediaPicker && ! mediaBankEntryId"
                        class="px-2 py-4 mx-2 bg-gray-100 border md:-mx-4 lg:-mx-6 md:px-4 lg:px-6 border-y rounded-wl"
                    >
                        <input-media-bank-gallery
                            type="image"
                            :multiple="false"
                            v-model:content="mediaBankEntryId"
                        />
                        <div
                            v-if="!mediaBankEntryId"
                            class="mt-2"
                        >
                            <button-secondary
                                v-if="showMediaPicker"
                                size="sm"
                                @click="showMediaPicker = false"
                            >Cancel</button-secondary>
                        </div>
                    </div>
                    <div
                        v-if="mediaBankEntryId"
                        class="overflow-hidden rounded-wl"
                    >
                        <media-bank-entries-display
                            :data-url="route('mediaBank.entries.show', mediaBankEntryId)"
                        />
                    </div>
                    <div
                        v-if="!mediaBankEntryId"
                        class="mt-2"
                    >
                        <button-secondary
                            v-if="! showMediaPicker"
                            size="sm"
                            @click="showMediaPicker = true"
                        >Add poster image</button-secondary>
                    </div>
                    <div
                        v-if="mediaBankEntryId"
                        class="flex justify-end mt-2"
                    >
                        <button-danger
                            size="sm"
                            @click="() => {mediaBankEntryId = null; showMediaPicker = false;}"
                        >Remove poster image</button-danger>
                    </div>
                </div>

                <h3 class="mb-4 text-xl font-bold">Competition Prizes</h3>

                <div class="mb-4">
                    <div v-for="(prize, prizeIndex) in sortedPrizes" :key="prize.id">
                        <card-sub class="mb-4">
                            <div>
                                <h4 class="text-lg font-semibold">Prize {{ prizeIndex + 1 }}</h4>
                                <p class="space-x-1 text-sm text-gray-600">
                                    <span>{{ prize.winners }} winner{{ prize.winners > 1 ? 's' : '' }}</span>
                                    <span>•</span>
                                    <span>{{ prize.proMonths }} Pro month{{ prize.proMonths > 1 ? 's' : '' }}</span>
                                    <span>•</span>
                                    <span>{{ prize.benefits.length }} benefit{{ prize.benefits.length === 1 ? '' : 's' }}</span>
                                    <span>•</span>
                                    <span>Value: €{{ calculatePrizeValue(prize).toFixed(2) }}</span>
                                </p>
                            </div>
                            <div class="flex justify-end space-x-2">
                                <button-danger
                                    v-if="prizes.length > 1"
                                    size="sm"
                                    @click="removePrize(prizeIndex)"
                                >
                                    <i class="fa-light fa-trash-can"></i>
                                </button-danger>
                                <button-base
                                    style-type="secondary"
                                    size="sm"
                                    @click="editPrize(prize)"
                                >
                                    <template #icon>
                                        <i class="mr-2 fa-light fa-edit"></i>
                                    </template>
                                    Edit
                                </button-base>
                            </div>
                        </card-sub>
                    </div>
                </div>

                <div class="text-lg font-bold text-center">
                    Total Competition Value: €{{ totalCompetitionValue.toFixed(2) }}
                </div>
                <div class="flex items-center justify-center mt-4 mb-8">
                    <button-primary
                        v-if="prizes.length < 5"
                        @click="addPrize"
                    >
                        <i class="mr-2 fa-light fa-trophy"></i> Add Prize
                    </button-primary>
                </div>
            </div>

            <div>
                <h3 class="mb-4 text-xl font-bold">Competition Settings</h3>

                <div class="mb-6">
                    <input-date
                        label="Competition End Date"
                        v-model:content="endDate"
                        :min="minEndDate"
                        required
                        class="!mb-0"
                    />
                </div>

                <div>
                    <h4 class="mb-4 text-lg font-semibold">Social Media Profiles</h4>
                    <p class="mb-3 text-sm text-gray-600">Select which social media profiles should be included in the competition:</p>

                    <div class="grid grid-cols-1 gap-4">
                        <card-sub
                            v-for="(profile, profileIndex) in socialMediaProfiles"
                            :key="profileIndex"
                        >
                            <div class="flex items-center">
                                <input
                                    type="checkbox"
                                    :id="`profile-${profileIndex}`"
                                    v-model="profile.included"
                                    class="w-4 h-4 mr-3"
                                />
                                <label :for="`profile-${profileIndex}`" class="flex items-center cursor-pointer">
                                    <i :class="profile.icon" class="mr-3 text-lg"></i>
                                    {{ profile.name }}
                                </label>
                            </div>
                            <div v-if="profile.included" class="mt-2 pl-7">
                                <input-text
                                    :label="`${profile.name} profile URL`"
                                    v-model:content="profile.url"
                                    :placeholder="`https://${profile.placeholder}`"
                                />
                            </div>
                        </card-sub>
                    </div>

                    <div class="mt-4">
                        <div class="flex items-center space-x-2">
                            <div class="flex-grow">
                                <input-text
                                    label="Add custom social media platform"
                                    v-model:content="customSocialMedia"
                                    placeholder="e.g., Snapchat, BeReal"
                                />
                            </div>
                            <button-base
                                class="mt-2"
                                :style-type="!customSocialMedia.trim() ? 'disabled' : 'primary'"
                                @click="addCustomSocialMedia"
                                :disabled="!customSocialMedia.trim()"
                            >
                                <template #icon>
                                    <i class="mr-2 fa-light fa-plus"></i>
                                </template>
                                Add
                            </button-base>
                        </div>
                    </div>
                </div>

                <div class="flex justify-end mt-6">
                    <button-with-confirmation
                        confirm-type="success"
                        confirm-text="You will not be able to alter the giveway once it is created. Are you sure you want to proceed?"
                        confirm-button-label="Create Giveaway"
                        abort-button-label="Cancel"
                        @confirmed="saveCompetition"
                        class="mb-1"
                    >
                        Create Giveaway
                    </button-with-confirmation>
                </div>
            </div>
        </div>

        <slideout-with-slots v-if="editingPrize" @close="closeEditor">
            <template #title>
                <div class="w-full pr-6">
                    <h3>Edit Prize</h3>
                    <div class="text-sm font-medium">
                        Current Value: €{{ calculatePrizeValue(editingPrize).toFixed(2) }}
                    </div>
                </div>
            </template>
            <template #body>
                <div>
                    <div class="grid grid-cols-1 gap-4 mb-4">
                        <input-number
                            label="Number of Winners"
                            min="1"
                            :max="100"
                            v-model:content="editingPrize.winners"
                            :show-step-ui="true"
                        />

                        <div class="text-sm text-gray-600">
                            {{ monthsRemaining - usedMonths - (editingPrize.proMonths * editingPrize.winners) }} months remaining
                        </div>

                        <input-number
                            :label="`myflow Pro Months (€149 each)`"
                            min="0"
                            :max="monthsRemaining - (usedMonths * editingPrize.winners)"
                            v-model:content="editingPrize.proMonths"
                            :show-step-ui="true"
                        />

                    </div>

                    <card-sub>
                        <div class="flex items-center justify-between">
                            <h5 class="font-medium">Additional Benefits</h5>
                            <div class="text-sm">
                                Benefits Value: €{{ calculateBenefitsValue(editingPrize).toFixed(2) }}
                            </div>
                        </div>

                        <div
                            v-for="(benefit, benefitIndex) in editingPrize.benefits"
                            :key="benefit.id"
                            class="flex items-center mt-3 mb-3"
                        >
                            <div class="grid flex-grow grid-cols-1 gap-2 mr-2 md:grid-cols-4">
                                <div class="md:col-span-3">
                                    <input-text
                                        :label="`Benefit ${benefitIndex + 1}`"
                                        v-model:content="benefit.description"
                                        placeholder="Describe the benefit (e.g., '1 hour coaching session')"
                                    />
                                </div>
                                <div class="md:col-span-1">
                                    <input-number
                                        label="Value (€)"
                                        min="0"
                                        v-model:content="benefit.value"
                                    />
                                </div>
                            </div>
                            <button-danger
                                class="-mb-2"
                                size="sm"
                                @click="removeBenefit(editingPrize, benefitIndex)"
                            >
                                <i class="fa-light fa-trash-can"></i>
                            </button-danger>
                        </div>

                        <div class="flex justify-end mt-3">
                            <button-base
                                v-if="editingPrize.benefits.length < 5"
                                style-type="primary"
                                size="sm"
                                @click="addBenefit(editingPrize)"
                            >
                                <template #icon>
                                    <i class="mr-2 fa-light fa-plus"></i>
                                </template>
                                Add Benefit
                            </button-base>
                        </div>
                    </card-sub>

                    <div class="flex justify-end mt-6">
                        <button-success
                            :disabled="! editingPrizeValidates"
                            @click="saveEditedPrize"
                        >
                            Save Changes
                        </button-success>
                    </div>
                </div>
            </template>
            <template #footer>
                <div class="flex justify-between p-4 space-x-2">
                    <button class="myflow-basic-button--secondary" @click="closeEditor">
                        Cancel
                    </button>
                    <button class="myflow-basic-button" @click="saveEditedPrize">
                        Save Prize
                    </button>
                </div>
            </template>
        </slideout-with-slots>
    </div>
</template>
